import { convertToHM, convertToLocaleDate, convertToLocaleTime, convertToYMD } from '../helpers/date.js';
import type { DeliveryAddress, DeliveryDate, Extras } from '../stores/store.type.js';

type TranslatableFields =
  | '_firstname'
  | '_lastname'
  | '_address'
  | '_city'
  | '_housenumber'
  | '_extension'
  | '_zipcode'
  | '_phone_recipient'
  | '_company'
  | '_mourning_date'
  | '_mourning_time';

type FuneralTranslatableFields = `_funeral${Exclude<TranslatableFields, '_mourning_date' | '_mourning_time'>}`;

export function getDeliveryAddress(deliveryAddress: DeliveryAddress): Record<string, string> {
  const address: Record<string, string> = {};

  if (deliveryAddress) {
    const { deliveryAddressType, firstName, lastName, street, houseNumber, postalCode, city, phoneNumber, country, ...optionalFields } =
      deliveryAddress;
    const isFuneral = deliveryAddressType === 'funeralParlor';
    address[getFieldKeyTranslation('_firstname', isFuneral)] = firstName;
    address._lastname = lastName;
    address[getFieldKeyTranslation('_lastname', isFuneral)] = lastName;
    address._firstname = firstName;
    address[getFieldKeyTranslation('_address', isFuneral)] = street;
    address._address = street;
    address[getFieldKeyTranslation('_housenumber', isFuneral)] = houseNumber;
    address._housenumber = houseNumber;
    address[getFieldKeyTranslation('_city', isFuneral)] = city;
    address._city = city;
    address._country = country;
    address[getFieldKeyTranslation('_zipcode', isFuneral)] = postalCode;
    address._zipcode = postalCode;
    address[getFieldKeyTranslation('_phone_recipient', isFuneral)] = phoneNumber;
    address._phone_recipient = phoneNumber;
    address._delivery_type = translateDeliveryAddressType(deliveryAddressType);

    if (optionalFields.houseNumberAddition) {
      address[getFieldKeyTranslation('_extension', isFuneral)] = optionalFields.houseNumberAddition;
      address._extension = optionalFields.houseNumberAddition;
    }

    if (optionalFields.company) {
      address[getFieldKeyTranslation('_company', isFuneral)] = optionalFields.company;
      address._company = optionalFields.company;
    }

    if (optionalFields.funeralDate && optionalFields.funeralTime) {
      address[getFieldKeyTranslation('_mourning_date', false)] = convertToLocaleDate(optionalFields.funeralDate);
      address._funeral_date = convertToYMD(optionalFields.funeralDate);
      address[getFieldKeyTranslation('_mourning_time', false)] = convertToLocaleTime(optionalFields.funeralTime);
      address._funeral_time = convertToHM(optionalFields.funeralTime);
    }
  }

  return address;
}

/**
 * @since 2026-03-07: This function is glue code to get the old card message. Later it should be refactored to get
 *  the card message from the store instead of the DOM. but this is a quick fix to get this working today.
 *
 * @fixme: Refactor this to not have logic to switch between message and lint
 */
export function getCardMessage(extras: Extras): Record<string, string> {
  const cardMessages: Record<string, string> = {};

  if (extras.cardOrLint === 'card') {
    const cardMessageElement = document.querySelector<HTMLTextAreaElement>("[name='properties[Tekst wenskaart]']");
    const cardMessage = extras.card?.message ?? cardMessageElement?.value;

    if (cardMessage) {
      cardMessages['Tekst wenskaart'] = cardMessage;
      cardMessages._card_message = cardMessage;
    }
  }

  if (extras.cardOrLint === 'lint') {
    const lintLeftElement = document.querySelector<HTMLInputElement>('[name="properties[Lint left]"]');
    const lintRightElement = document.querySelector<HTMLInputElement>('[name="properties[Lint right]"]');

    cardMessages['Lint left'] = lintLeftElement?.value ?? '';
    cardMessages._lint_left_text = lintLeftElement?.value ?? '';
    cardMessages['Lint right'] = lintRightElement?.value ?? '';
    cardMessages._lint_right_text = lintRightElement?.value ?? '';
  }

  return cardMessages;
}

export function getDeliveryDateAttributes(deliveryDate: DeliveryDate): Record<string, string> {
  const date: Record<string, string> = {};

  if (deliveryDate) {
    date._DeliveryDate = convertToYMD(deliveryDate.date);
    date.Date = convertToLocaleDate(deliveryDate.date);
  }

  return date;
}

function translateDeliveryAddressType(deliveryAddressType: string): 'woonadres' | 'zakelijk' | 'rouwcentrum' {
  switch (deliveryAddressType) {
    case 'company':
      return 'zakelijk';
    case 'funeralParlor':
      return 'rouwcentrum';
    case 'home':
    default:
      return 'woonadres';
  }
}

function getFieldKeyTranslation(key: TranslatableFields, isFuneral = false): string {
  let findKey: unknown = key;
  const translations: Record<TranslatableFields & FuneralTranslatableFields, string> = JSON.parse(
    document.querySelector<HTMLScriptElement>('#order-form-attributes')?.textContent ?? '{}',
  );

  if (isFuneral) {
    findKey = `_funeral${key}`;
  }

  return translations[findKey as TranslatableFields & FuneralTranslatableFields];
}

/**
 * Determines the product ID from the DOM, this uses either the variant selector or the hidden input field as fallback.
 */
export function determineProductId() {
  return document.querySelector<HTMLInputElement>('[type="radio"][name="id"]:checked,[type="hidden"][name="id"]')?.value;
}

/**
 * Determines the selected quantity for the main product from the DOM,
 * it uses the quantity input field if available, or defaults to 1 if the element is not present.
 *
 * Considers the min and max attributes of the quantity input to ensure the returned quantity
 * is within the allowed range.
 *
 * @return {number} The selected quantity for the main product, constrained by min and max values if applicable.
 */
export function getMainProductQuantity(): number {
  const quantityElement = document.querySelector<HTMLInputElement>('[name="quantity"]');
  if (quantityElement) {
    const minQuantity = Number.parseInt(quantityElement.min, 10) || 1;
    const maxQuantity = Number.parseInt(quantityElement.max, 10) || Infinity;
    const defaultQuantity = Number.parseInt(quantityElement.value, 10) || minQuantity;

    return Math.max(minQuantity, Math.min(defaultQuantity, maxQuantity));
  }

  return 1;
}
